import type { ReactNode, FC } from 'react';
import { useRef } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import ScrollableContent from '@wix/da-ds/pkg/ScrollableContent';
import Loader from '@wix/da-ds/pkg/Loader';
import Pagination from '../../../StudioPagination';
import StudioTableBatchBanner from '../../../StudioTableBatchBanner';
import SelectableDeviationList from '../../_partials/SelectableDeviationList';

import s from './DeviationsTab.scss';

interface Props {
  deviations: PapiDeviation[];
  isFetching: boolean;
  selected: number[];
  hasSelection: boolean;
  /** an optional rendered component that precedes all the items in the deviations list */
  elementBeforeList?: ReactNode;
  onSelect: (idx: number, isSelected: boolean) => void;
}

const DeviationsTab: FC<Props> = ({
  deviations,
  isFetching,
  selected,
  hasSelection,
  elementBeforeList,
  onSelect,
}) => {
  const scrollableRef = useRef<HTMLElement | null>(null);

  const scrollToTop = () => {
    scrollableRef.current && scrollableRef.current.scrollTo({ top: 0 });
  };

  return (
    <>
      <StudioTableBatchBanner />
      <ScrollableContent
        wrapperClassName={s['content']}
        innerRef={el => (scrollableRef.current = el)}
      >
        {elementBeforeList}
        <SelectableDeviationList
          deviations={deviations}
          hasSelection={hasSelection}
          selected={selected}
          scrollableRef={scrollableRef}
          onSelect={onSelect}
        />
      </ScrollableContent>

      {isFetching && (
        <div className={s['loading']}>
          <Loader state="loading" size="big" />
        </div>
      )}

      {!isFetching && !hasSelection && <Pagination onFetchSuccess={scrollToTop} />}
    </>
  );
};
DeviationsTab.displayName = 'DeviationsTab';

export default DeviationsTab;
